import React, { useEffect } from "react";
import {
  methodTypeToRecoveryKey,
  recoveryKeyToMethodType,
  recoveryLostMethodTitle,
  recoveryMethodObject,
} from "./loginVerifyHelpers";
import "./loginVerifyFlow.css";

const METHOD_META = {
  totp: {
    title: "Google Authenticator",
    fallbackSub: "Use your authenticator app",
    iconSrc: "/images/security/authenticator.svg",
  },
  email: {
    title: "Email OTP",
    fallbackSub: "Receive a 6-digit code by email",
    iconSrc: "/images/email_vector2.svg",
  },
  phone: {
    title: "Phone Number",
    fallbackSub: "Receive a 6-digit code by SMS",
    iconSrc: "/images/security/phone.svg",
  },
};

function methodSub(key, availableMethods) {
  const type = recoveryKeyToMethodType(key);
  const existing = (availableMethods || []).find((m) => Number(m?.type) === type);
  if (existing?.maskedValue) return existing.maskedValue;
  return METHOD_META[key]?.fallbackSub || "";
}

export default function LoginMethodRecoveryOverlays({
  step,
  recoverableMethods = [],
  selectedLost = "",
  availableMethods = [],
  remainingKeys = [],
  verifiedKeys = [],
  verifyType,
  otpValue = "",
  resendTimer = 0,
  verifyBusy = false,
  startBusy = false,
  resetAck = false,
  restrictionHours = 24,
  onSelectLost,
  onResetAck,
  onClose,
  onSelectConfirm,
  onWarnCancel,
  onWarnConfirm,
  onPickRemaining,
  onRequirementsBack,
  onVerifySubmit,
  onGetCode,
  onPaste,
  onOtpChange,
  onSuccessDone,
}) {
  useEffect(() => {
    if (!step) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, [step]);

  if (!step) return null;

  const lostTitle = recoveryLostMethodTitle(selectedLost);
  const verifyKey = methodTypeToRecoveryKey(verifyType) || remainingKeys[0] || "";
  const verifyObj = recoveryMethodObject(verifyKey, availableMethods);
  const verifyMasked = verifyObj?.maskedValue || methodSub(verifyKey, availableMethods);
  const isTotpVerify = verifyKey === "totp";
  const isPhoneVerify = verifyKey === "phone";
  const otpDigits = String(otpValue || "").replace(/\D/g, "").slice(0, 6);
  const otpComplete = otpDigits.length >= 6;
  const getCodeLabel = resendTimer > 0 ? `${resendTimer}s` : "Get Code";
  const requirementKeys = [...new Set([...(verifiedKeys || []), ...(remainingKeys || [])])];
  const reqDone = (verifiedKeys || []).length;
  const reqTotal = requirementKeys.length || remainingKeys.length;

  const verifyHeading = isTotpVerify
    ? "Authenticator App Verification"
    : isPhoneVerify
      ? "Phone Verification"
      : "Email Verification";
  const verifySubtitle = isTotpVerify
    ? "Enter the 6-digit code generated by your authenticator app to remove this method."
    : `Enter the 6-digit verification code sent to ${verifyMasked || "your remaining method"} to remove ${lostTitle}.`;
  const verifyLabel = isTotpVerify
    ? "Authenticator App"
    : isPhoneVerify
      ? "Phone Verification Code"
      : "Email Verification Code";

  return (
    <div className="lmv-rec-overlay" role="presentation">
      {step === "select" ? (
        <div className="lmv-rec-modal" role="dialog" aria-modal="true" aria-labelledby="lmv-rec-select-title">
          <button type="button" className="lmv-rec-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
          <h2 id="lmv-rec-select-title" className="lmv-rec-title">
            Select Unavailable Methods
          </h2>
          <p className="lmv-rec-sub">
            Choose the security method you can no longer access. You will verify with your remaining method next.
          </p>
          <div className="lmv-rec-list" role="list">
            {recoverableMethods.map((key) => {
              const copy = METHOD_META[key] || METHOD_META.totp;
              const selected = selectedLost === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="listitem"
                  className={`lmv-rec-item${selected ? " is-selected" : ""}`}
                  onClick={() => onSelectLost?.(key)}
                >
                  <span className="lmv-rec-item__left">
                    <span className="lmv-rec-item__icon" aria-hidden>
                      <img src={copy.iconSrc} alt="" />
                    </span>
                    <span className="lmv-rec-item__text">
                      <strong>{copy.title}</strong>
                      <span>{methodSub(key, availableMethods)}</span>
                    </span>
                  </span>
                  <span className={`lmv-rec-check${selected ? " is-checked" : ""}`} aria-hidden />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="lmv-rec-primary"
            disabled={!selectedLost || startBusy}
            onClick={onSelectConfirm}
          >
            Continue
          </button>
        </div>
      ) : null}

      {step === "warn" ? (
        <div
          className="lmv-rec-modal lmv-rec-modal--warn"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lmv-rec-warn-title"
        >
          <button type="button" className="lmv-rec-close" aria-label="Close" onClick={onWarnCancel}>
            ×
          </button>
          <div className="lmv-rec-warn-hero" aria-hidden="true">
            <img className="dark_img" src="/images/security/security_vector2.svg" alt="" />
            <img className="light_img" src="/images/security/security_vector_light2.svg" alt="" />
          </div>
          <h2 id="lmv-rec-warn-title" className="lmv-rec-title lmv-rec-title--center">
            Reset {lostTitle}?
          </h2>
          <p className="lmv-rec-sub lmv-rec-sub--center">
            This turns off {lostTitle} for login. You must prove your remaining method first.
          </p>
          <label className="lmv-rec-ack">
            <input
              type="checkbox"
              className="lmv-rec-ack__input"
              checked={resetAck}
              onChange={(e) => onResetAck?.(e.target.checked)}
            />
            <span className={`lmv-rec-ack__box${resetAck ? " is-checked" : ""}`} aria-hidden="true" />
            <span>
              For your protection, withdrawals, P2P selling, and payments may be paused for {restrictionHours} hours
              after you make this change.
            </span>
          </label>
          <div className="lmv-rec-actions">
            <button type="button" className="lmv-rec-ghost" onClick={onWarnCancel}>
              Cancel
            </button>
            <button
              type="button"
              className={`lmv-rec-primary lmv-rec-primary--inline${resetAck ? " is-enabled" : ""}`}
              disabled={!resetAck || startBusy}
              onClick={onWarnConfirm}
            >
              {startBusy ? "Starting…" : "Confirm"}
            </button>
          </div>
        </div>
      ) : null}

      {step === "requirements" ? (
        <div
          className="lmv-rec-modal lmv-rec-modal--req"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lmv-rec-req-title"
        >
          <div className="lmv-rec-req-topbar">
            <button type="button" className="lmv-rec-back" aria-label="Back" onClick={onRequirementsBack}>
              <i className="ri-arrow-left-long-line" aria-hidden="true" />
            </button>
            <button type="button" className="lmv-rec-close lmv-rec-close--inline" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
          <h2 id="lmv-rec-req-title" className="lmv-rec-title">
            Verify remaining methods
          </h2>
          <p className="lmv-rec-sub">
            Complete the following verification to remove {lostTitle}. Then you will return to login.
          </p>
          <div className="lmv-rec-req-progress">
            {reqDone} / {reqTotal || 1}
          </div>
          <div className="lmv-rec-req-list" role="list">
            {requirementKeys.map((key) => {
              const copy = METHOD_META[key] || METHOD_META.totp;
              const done = (verifiedKeys || []).includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  role="listitem"
                  className={`lmv-rec-req-item${done ? " is-done" : ""}`}
                  disabled={done}
                  onClick={() => {
                    if (!done) onPickRemaining?.(key);
                  }}
                >
                  <span className="lmv-rec-item__left">
                    <span className="lmv-rec-item__icon" aria-hidden>
                      <img src={copy.iconSrc} alt="" />
                    </span>
                    <span className="lmv-rec-item__text">
                      <strong>{copy.title}</strong>
                      <span>{methodSub(key, availableMethods)}</span>
                    </span>
                  </span>
                  <span className="lmv-rec-req-right" aria-hidden>
                    {done ? <i className="ri-check-line" /> : <i className="ri-arrow-right-long-line" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {step === "verify" ? (
        <div
          className="lmv-rec-modal lmv-rec-modal--verify"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lmv-rec-verify-title"
        >
          <button type="button" className="lmv-rec-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
          <h2 id="lmv-rec-verify-title" className="lmv-rec-title">
            {verifyHeading}
          </h2>
          <p className="lmv-rec-sub">{verifySubtitle}</p>
          <div className="lmv-rec-field-label">{verifyLabel}</div>
          <div className="lmv-rec-input-row">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              value={otpDigits}
              onChange={(e) => onOtpChange?.(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              onPaste={(e) => {
                const text = e.clipboardData?.getData("text") || "";
                const raw = text.replace(/\D/g, "").slice(0, 6);
                if (!raw) return;
                e.preventDefault();
                onOtpChange?.(raw);
              }}
              aria-label={verifyLabel}
            />
            {isTotpVerify ? (
              <button type="button" className="lmv-rec-input-action" onClick={onPaste}>
                Paste
              </button>
            ) : (
              <button
                type="button"
                className="lmv-rec-input-action"
                disabled={resendTimer > 0}
                onClick={onGetCode}
              >
                {getCodeLabel}
              </button>
            )}
          </div>
          <button
            type="button"
            className={`lmv-rec-primary${otpComplete && !verifyBusy ? " is-enabled" : ""}`}
            disabled={verifyBusy || !otpComplete}
            onClick={() => onVerifySubmit?.(otpDigits)}
          >
            {verifyBusy ? "Verifying…" : "Confirm"}
          </button>
          <div className="lmv-rec-footer">
            <img src="/images/protected_icon.svg" alt="" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      ) : null}

      {step === "success" ? (
        <div
          className="lmv-rec-modal lmv-rec-modal--success"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lmv-rec-ok-title"
        >
          <div className="lmv-rec-success-hero" aria-hidden="true">
            <img src="/images/sucessfull_payment_icon.svg" alt="" />
          </div>
          <h2 id="lmv-rec-ok-title" className="lmv-rec-title lmv-rec-title--center">
            Remaining methods verified
          </h2>
          <p className="lmv-rec-sub lmv-rec-sub--center">
            {lostTitle} has been removed. Redirecting you to login…
          </p>
          <button type="button" className="lmv-rec-primary" onClick={onSuccessDone}>
            Back to login
          </button>
        </div>
      ) : null}
    </div>
  );
}
